/**
 * Lu Moments entrance reveals.
 *
 * Two treatments:
 *  - Groups: [data-lm-reveal] with direct children [data-lm-reveal-child], staggered fade and rise.
 *  - Headlines: [data-lm-lines] is split into lines, each lifting out of its own clipped box.
 *
 * Each element reveals once, when it reaches 85% of the viewport height, and is then forgotten.
 * Anything already level with or above the viewport reveals immediately, so a restored scroll
 * position or an anchor jump never leaves a section invisible.
 * Under reduced motion nothing is split and everything is shown at once.
 * The hidden start states live behind the `js` class, so with JavaScript off the page renders at rest.
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const GROUP = '[data-lm-reveal]';
const SINGLE = '[data-lm-reveal-self]';
// Reveal children sit inside each section's content wrapper, not directly under the group,
// so this is a descendant match. Sections never nest groups, so there is nothing to exclude.
const CHILD = ':scope [data-lm-reveal-child]';
const LINES = '[data-lm-lines]';

/**
 * Horizon scrolls `.page-wrapper` on desktop and the document on phones, and an element clipped
 * by that container does not always produce an IntersectionObserver callback (an instant jump or a
 * restored scroll position can skip the state change entirely). A direct sweep is deterministic
 * and cheap, so it drives the reveal and nothing can be left parked invisible.
 */
const REVEAL_RATIO = 0.85;

/** @type {Set<Element>} */
const pending = new Set();
let sweepQueued = false;

function reveal(el) {
  el.classList.add('is-revealed');
  pending.delete(el);
}

function revealAll(root = document) {
  root.querySelectorAll(`${GROUP}, ${SINGLE}`).forEach(reveal);
}

function sweep() {
  sweepQueued = false;
  if (!pending.size) return;
  const limit = window.innerHeight * REVEAL_RATIO;
  for (const el of [...pending]) {
    const { top, bottom } = el.getBoundingClientRect();
    // In view, or already scrolled past: either way it must be shown.
    if (top < limit && bottom > 0) reveal(el);
    else if (bottom <= 0) reveal(el);
  }
  if (!pending.size) teardown();
}

function queueSweep() {
  if (sweepQueued) return;
  sweepQueued = true;
  // An animation frame keeps this off the scroll critical path. A throttled or background tab
  // gets no frames, so a timer backs it up; sweep() clears the flag, so only the first one works.
  requestAnimationFrame(sweep);
  setTimeout(() => {
    if (sweepQueued) sweep();
  }, 250);
}

function scrollTargets() {
  const wrapper = document.querySelector('.page-wrapper');
  return wrapper ? [window, wrapper] : [window];
}

function teardown() {
  for (const target of scrollTargets()) target.removeEventListener('scroll', queueSweep);
  window.removeEventListener('resize', queueSweep);
}

function listen() {
  for (const target of scrollTargets()) {
    target.removeEventListener('scroll', queueSweep);
    target.addEventListener('scroll', queueSweep, { passive: true });
  }
  window.removeEventListener('resize', queueSweep);
  window.addEventListener('resize', queueSweep, { passive: true });
}

const escapeHtml = (value) =>
  value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });

/**
 * Splits an element's text into one clipped box per rendered line.
 * The original text is kept on the element so a resize can re-split from a clean state.
 * @param {HTMLElement} el
 */
function splitLines(el) {
  const source = el.dataset.lmText ?? el.textContent ?? '';
  const text = source.trim();
  if (!text) return;
  el.dataset.lmText = text;

  const words = text.split(/\s+/);
  // Measure pass: each word in its own span so we can read which line it landed on.
  el.innerHTML = words.map((word) => `<span data-lm-word>${escapeHtml(word)}</span>`).join(' ');

  /** @type {string[][]} */
  const lines = [];
  let currentTop = null;
  /** @type {string[]} */
  let current = [];

  for (const span of el.querySelectorAll('[data-lm-word]')) {
    const top = Math.round(/** @type {HTMLElement} */ (span).offsetTop);
    const word = span.textContent ?? '';
    if (currentTop === null || Math.abs(top - currentTop) <= 3) {
      if (currentTop === null) currentTop = top;
      current.push(word);
    } else {
      lines.push(current);
      current = [word];
      currentTop = top;
    }
  }
  if (current.length) lines.push(current);

  el.innerHTML = lines
    .map(
      (line, index) =>
        `<span class="lm-line"><span class="lm-line__inner" style="--lm-line-index:${index}">${escapeHtml(
          line.join(' ')
        )}</span></span>`
    )
    .join('');
  el.classList.add('is-split');
}

/** @param {HTMLElement} el */
function unsplitLines(el) {
  if (el.dataset.lmText === undefined) return;
  el.textContent = el.dataset.lmText;
  el.classList.remove('is-split');
}

/** @param {ParentNode} root */
function setupLines(root = document) {
  const targets = /** @type {NodeListOf<HTMLElement>} */ (root.querySelectorAll(LINES));
  for (const el of targets) {
    if (reducedMotion.matches) unsplitLines(el);
    else splitLines(el);
  }
}

/**
 * @param {ParentNode} root
 */
export function lmReveal(root = document) {
  if (reducedMotion.matches) {
    setupLines(root);
    revealAll(root);
    return;
  }

  setupLines(root);

  root.querySelectorAll(GROUP).forEach((group) => {
    if (group.classList.contains('is-revealed')) return;
    group.querySelectorAll(CHILD).forEach((child, index) => {
      /** @type {HTMLElement} */ (child).style.setProperty('--lm-reveal-index', String(index));
    });
    pending.add(group);
  });
  root.querySelectorAll(SINGLE).forEach((el) => {
    if (!el.classList.contains('is-revealed')) pending.add(el);
  });

  listen();
  // Run the first pass synchronously: a page opened in a background tab has no animation frames,
  // and the at-rest state must still be correct the moment it is looked at.
  sweep();
}

// Line breaks change with the viewport, so re-split after a resize settles.
let resizeTimer = 0;
let lastWidth = window.innerWidth;
window.addEventListener(
  'resize',
  () => {
    if (window.innerWidth === lastWidth) return; // ignore mobile address-bar height changes
    lastWidth = window.innerWidth;
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => setupLines(), 200);
  },
  { passive: true }
);

reducedMotion.addEventListener('change', () => {
  setupLines();
  if (reducedMotion.matches) revealAll();
});

// The theme editor re-renders sections: arm anything new.
document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {CustomEvent} */ (event).target;
  if (target instanceof HTMLElement) lmReveal(target);
});

function start() {
  // Split after webfonts settle, so line breaks are measured against the real face.
  if (document.fonts?.status === 'loaded') lmReveal();
  else document.fonts?.ready.then(() => lmReveal()).catch(() => lmReveal()) ?? lmReveal();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
else start();
